import type {
  EmissionReportCellValue,
  EmissionReportData,
  EmissionReportMeasurement,
} from '../../types/entities';

/**
 * Texte des Tooltips einer leeren Zelle (`cellTitle`) -- aus `EmissionReportMatrix.tsx` hierher
 * verschoben (Plan 40-08), zusammen mit den drei Zellhilfen, die sie brauchen.
 */
const EMPTY_REASON_TEXT: Record<string, string> = {
  probe: 'An diesem Messtag wurde keine Probe genommen.',
  nn: 'Marker „n.n.“ im Bericht — nicht nachweisbar.',
  nb: 'Marker „n.b.“ im Bericht — nicht berechenbar.',
  blimit: 'Marker „b“ im Bericht — unterhalb der Bestimmungsgrenze, ohne Grenzwert im Bericht.',
  unread: 'Vom Import nicht gelesen.',
  hand: 'In dieser Vorschau von Hand geleert.',
};

/**
 * Reine Bearbeitungsoperationen auf `resultData` (Plan 35-12, Sketch 020). Kein React-, Router-
 * oder Mantine-Bezug -- jede Funktion liefert ein NEUES Dokument (bzw. eine neue Zelle), das
 * Eingabedokument bleibt unveraendert.
 */

export interface CellEdit {
  value: string | null;
  belowLimit: boolean;
}

/**
 * Setzt eine Zelle. Ein geleertes Feld (`value === null` oder leerer String) bekommt
 * `belowLimit: false` und als Grund `hand` -- es sei denn, die Zelle stand VOR dieser
 * Bearbeitung (im uebergebenen `data`) schon leer: dann behaelt sie ihren bisherigen Grund
 * (Sketch 020, `commitEdit()`: "War das Feld schon im Bericht leer, behaelt es seinen
 * urspruenglichen Grund"). Ein gefuelltes Feld verliert seinen Grund (`empty: null`).
 *
 * Der Aufrufer traegt dafuer Sorge, dass `data` den Stand VOR dieser einen Bearbeitung zeigt --
 * die Komponente ruft diese Funktion deshalb mit einem Schnappschuss der Zelle auf, wie sie beim
 * Beginn der Bearbeitung stand (siehe `EmissionReportMatrix.tsx`), nicht mit dem waehrend des
 * Tippens schon live veraenderten Formularwert.
 */
export function setCell(
  data: EmissionReportData,
  measurementIndex: number,
  fieldKey: string,
  edit: CellEdit
): EmissionReportData {
  const value = edit.value === '' ? null : edit.value;
  return {
    ...data,
    measurements: data.measurements.map((measurement, index) => {
      if (index !== measurementIndex) {
        return measurement;
      }
      const currentCell = measurement.values[fieldKey];
      const nextCell: EmissionReportCellValue =
        value === null
          ? {
              value: null,
              belowLimit: false,
              empty: currentCell?.value === null ? currentCell.empty : 'hand',
            }
          : { value, belowLimit: edit.belowLimit, empty: null };
      return {
        ...measurement,
        values: { ...measurement.values, [fieldKey]: nextCell },
      };
    }),
  };
}

/**
 * Vergleicht Wert UND Kennzeichen einer Messwertzelle gegen `sourceData` -- die EINZIGE Quelle
 * der Aenderungsmarkierung (D-05). Der Grund (`empty`) fliesst NICHT in den Vergleich ein: Wer
 * einen Wert aendert und wieder auf den urspruenglichen zurueckstellt, ist die Markierung wieder
 * los, auch wenn der Grund intern jetzt `hand` statt des urspruenglichen Import-Grundes ist --
 * `empty` wirkt sich ohnehin nur aus, solange `value === null` ist (siehe `previewStats.ts`).
 * Der Vergleich passiert ausschliesslich beim Anzeigen und wird nirgends gespeichert.
 */
export function isChanged(
  source: EmissionReportData,
  result: EmissionReportData,
  measurementIndex: number,
  fieldKey: string
): boolean {
  const sourceCell = source.measurements[measurementIndex]?.values[fieldKey];
  const resultCell = result.measurements[measurementIndex]?.values[fieldKey];
  if (!sourceCell || !resultCell) {
    return false;
  }
  return (
    !sameValue(sourceCell.value, resultCell.value) ||
    sourceCell.belowLimit !== resultCell.belowLimit
  );
}

/**
 * Setzt einen Massenstrom-Einzelwert (Phase 40, Plan 40-08, D-17) -- exakt dieselbe Semantik wie
 * `setCell` (siehe dort), nur adressiert ueber Komponentenschluessel und Eintragsindex statt ueber
 * Messungsindex und Feldschluessel. Fehlt die Komponente in `data.massFlows` (kein Katalogeintrag
 * fuer diesen Bericht, D-16, oder `data.massFlows` steht noch als `[]`), kommt `data` unveraendert
 * zurueck -- es gibt nichts zu bearbeiten.
 */
export function setMassFlowCell(
  data: EmissionReportData,
  componentKey: string,
  entryIndex: number,
  edit: CellEdit
): EmissionReportData {
  const massFlows = Array.isArray(data.massFlows) ? {} : (data.massFlows ?? {});
  const massFlow = massFlows[componentKey];
  if (!massFlow) {
    return data;
  }
  const value = edit.value === '' ? null : edit.value;
  return {
    ...data,
    massFlows: {
      ...massFlows,
      [componentKey]: {
        ...massFlow,
        entries: massFlow.entries.map((entry, index) => {
          if (index !== entryIndex) {
            return entry;
          }
          return value === null
            ? {
                ...entry,
                value: null,
                belowLimit: false,
                empty: entry.value === null ? entry.empty : 'hand',
              }
            : { ...entry, value, belowLimit: edit.belowLimit, empty: null };
        }),
      },
    },
  };
}

function massFlowEntry(
  data: EmissionReportData,
  componentKey: string,
  entryIndex: number
): { value: string | null; belowLimit: boolean } | undefined {
  const massFlows = Array.isArray(data.massFlows) ? undefined : data.massFlows;
  return massFlows?.[componentKey]?.entries[entryIndex];
}

/**
 * Vergleicht Wert UND Kennzeichen eines Massenstrom-Einzelwerts gegen `sourceData` -- dieselbe
 * Semantik wie `isChanged` (D-17: "derselbe Zellmechanismus"). Fehlt der Eintrag auf einer der
 * beiden Seiten (Komponente oder Index nicht vorhanden), gilt er als unveraendert.
 */
export function isMassFlowChanged(
  source: EmissionReportData,
  result: EmissionReportData,
  componentKey: string,
  entryIndex: number
): boolean {
  const sourceEntry = massFlowEntry(source, componentKey, entryIndex);
  const resultEntry = massFlowEntry(result, componentKey, entryIndex);
  if (!sourceEntry || !resultEntry) {
    return false;
  }
  return (
    !sameValue(sourceEntry.value, resultEntry.value) || sourceEntry.belowLimit !== resultEntry.belowLimit
  );
}

/**
 * Werte stehen als Zeichenkette im Dokument -- der Bericht liefert "4.0", das Zahlenfeld gibt
 * denselben Wert als "4" zurueck. Ein strikter Zeichenkettenvergleich haelte das fuer eine
 * Aenderung, und die Markierung waere nach jeder Bearbeitung nicht mehr loszuwerden: "4.0" ist
 * ueber ein Zahlenfeld gar nicht wieder eintragbar. D-05 verlangt die ECHTE Abweichung, also
 * wird numerisch verglichen, sobald beide Seiten Zahlen sind -- sonst als Text (Werte wie
 * Marker oder Freitext bleiben so exakt verglichen).
 */
function sameValue(a: string | null, b: string | null): boolean {
  if (a === b) {
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  const na = Number(a);
  const nb = Number(b);
  if (a.trim() === '' || b.trim() === '' || !Number.isFinite(na) || !Number.isFinite(nb)) {
    return false;
  }
  return na === nb;
}


/** Ob eine Messung als geloescht markiert ist (Punkt 4, WR-15/WR-12: `excluded === true`). */
export function isExcluded(measurement: EmissionReportMeasurement): boolean {
  return measurement.excluded === true;
}

/**
 * Ob noch eine Messung geloescht werden darf -- mindestens EINE nicht ausgeschlossene Messung
 * muss stehen bleiben (Punkt 4). Vor der Umstellung zaehlte die Laenge des Arrays; ausgeschlossene
 * Messungen bleiben aber im Array stehen, deshalb zaehlt hier ausschliesslich, wie viele davon
 * noch NICHT ausgeschlossen sind.
 */
export function canRemoveMeasurement(data: EmissionReportData): boolean {
  return data.measurements.filter((measurement) => !isExcluded(measurement)).length > 1;
}

/**
 * Markiert eine Messung als geloescht (`excluded: true`), statt sie zu entfernen (Punkt 4,
 * WR-15/WR-12 -- Abweichung von Sketch 020). Positionsbezuege (Aenderungsmarkierung,
 * Bearbeitungszustand, Protokoll-Messungsnummern) bleiben dadurch stabil: das Array behaelt
 * Laenge UND Reihenfolge, es wird nicht neu durchnummeriert. Ist die Messung an `position` schon
 * ausgeschlossen oder ist `canRemoveMeasurement` falsch (letzte verbliebene), kommt `data`
 * unveraendert zurueck -- die Komponente fragt zusaetzlich vorher `canRemoveMeasurement`, dieser
 * Guard ist die zweite, robuste Absicherung.
 */
export function removeMeasurement(data: EmissionReportData, position: number): EmissionReportData {
  const target = data.measurements[position];
  if (!target || isExcluded(target) || !canRemoveMeasurement(data)) {
    return data;
  }
  return {
    ...data,
    measurements: data.measurements.map((measurement, i) =>
      i === position ? { ...measurement, excluded: true } : measurement
    ),
  };
}

/**
 * Holt eine zuvor als geloescht markierte Messung an `position` zurueck -- entfernt den
 * Schluessel `excluded` wieder (neues Objekt ohne ihn, statt ihn auf `false` zu setzen), damit
 * der Stand wieder strukturgleich zu `sourceData` ist. Zellwerte und alle Bearbeitungen an der
 * Messung bleiben unberuehrt, weil sie die ganze Zeit im Dokument standen.
 */
export function restoreMeasurement(data: EmissionReportData, position: number): EmissionReportData {
  return {
    ...data,
    measurements: data.measurements.map((measurement, i) => {
      if (i !== position) {
        return measurement;
      }
      const restored: EmissionReportMeasurement = { ...measurement };
      delete restored.excluded;
      return restored;
    }),
  };
}

/** Positionen der ausgeschlossenen Messungen, aufsteigend -- Grundlage des Rueckgaengig-Stapels. */
export function excludedPositions(data: EmissionReportData): number[] {
  return data.measurements.reduce<number[]>((positions, measurement, i) => {
    if (isExcluded(measurement)) {
      positions.push(i);
    }
    return positions;
  }, []);
}

// --- Aus EmissionReportMatrix.tsx verschoben (Plan 40-08, reine Verlagerung ohne
// Verhaltensaenderung) -- 40-09 braucht dieselben drei Zellhilfen fuer die Massenstrom-Tabelle,
// statt sie ein zweites Mal nachzubauen. ------------------------------------------------------

function dummyMeasurement(values: Record<string, EmissionReportCellValue>): EmissionReportMeasurement {
  return {
    index: 0,
    date: null,
    dateRaw: null,
    operatingMode: null,
    time: null,
    valueBasis: null,
    oxygenReference: null,
    values,
  };
}

/**
 * Wendet `setCell` auf EINE Zelle an, ohne dass der Aufrufer ein volles Dokument mitfuehren
 * muesste -- `setCell` braucht fuer seine "war schon leer"-Regel ausschliesslich die Zelle, wie
 * sie VOR dieser Bearbeitung stand.
 *
 * Der Aufrufer ruft diese Funktion mit einem Schnappschuss der Zelle auf, wie sie beim Beginn der
 * Bearbeitung stand (siehe `EmissionReportMatrix.tsx`), nicht mit dem waehrend des Tippens schon
 * live veraenderten Formularwert.
 */
export function applyCellEdit(
  previousCell: EmissionReportCellValue,
  edit: { value: string | null; belowLimit: boolean }
): EmissionReportCellValue {
  const probe: EmissionReportData = {
    schemaVersion: 0,
    readable: true,
    measurementRange: { start: null, end: null },
    log: [],
    measurements: [dummyMeasurement({ cell: previousCell })],
  };
  return setCell(probe, 0, 'cell', edit).measurements[0].values.cell;
}

/**
 * Anzeige eines Dezimalwerts aus dem Importdokument mit Dezimalkomma (Sketch 020). Das Dokument
 * haelt Werte als API-String mit Punkt; hier wird nur das Trennzeichen getauscht, NICHT gerundet
 * oder gruppiert -- die Vorschau zeigt jede Stelle, die im Bericht steht bzw. die der Server beim
 * Mitteln liefert.
 */
export function decimalText(value: string): string {
  return value.replace('.', ',');
}

export function cellText(cell: EmissionReportCellValue): string {
  if (cell.value === null) {
    return '—';
  }
  return (cell.belowLimit ? '< ' : '') + decimalText(cell.value);
}

export function cellTitle(cell: EmissionReportCellValue): string {
  if (cell.value !== null) {
    return cell.belowLimit ? 'unter der Bestimmungsgrenze' : '';
  }
  return (cell.empty && EMPTY_REASON_TEXT[cell.empty]) || '';
}
